// src/pages/MembershipApply.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, FileUp, Loader2, UploadCloud, X, Trash2,
  File as FileIcon, FileText, FileArchive, FileSpreadsheet, Image as ImageIcon,
  Building2, IdCard, Globe, MapPin, Users2, Briefcase, CalendarClock,
  Mail, Phone, User as UserIcon, Landmark, ChevronDown, Sparkles, HandCoins
} from "lucide-react";
import Swal from "sweetalert2";
import Footer from "../components/Footer";
import { industries, periodicities, companySizes, workersRanges } from "../constants/options";
import { regionsData } from "../constants/regiones";

/* ========== Mini helper de transición en montaje ========== */
function Appear({ children, delay = 0, from = "translate-y-3", className = "" }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const r = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(r);
  }, []);
  return (
    <div
      className={`transform transition-all duration-500 ease-out will-change-[opacity,transform] ${
        mounted ? "opacity-100 translate-y-0" : `opacity-0 ${from}`
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ---------- helpers ---------- */
const clean = (s) => (s || "").toString().trim();
const formatBytes = (n = 0) => {
  if (!n) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};
const ext = (name = "") => (name.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || "").slice(1);
const isPdf = (f) => !!f && (f.type === "application/pdf" || /\.pdf$/i.test(f.name || ""));
const isJpgPng = (f) =>
  !!f &&
  (f.type === "image/png" ||
    f.type === "image/jpeg" ||
    /\.(png|jpg|jpeg)$/i.test(f.name || ""));

/** RUT visible: sin puntos, con guion, DV en mayúscula */
const formatRUT = (value = "") => {
  const v = value.replace(/[^0-9kK]/g, "").toUpperCase();
  if (!v) return "";
  if (v.length === 1) return v;
  const body = v.slice(0, -1);
  const dv = v.slice(-1);
  return `${body}-${dv}`;
};

/** Normaliza RUT para envío: sin puntos y con guion */
const normalizeRut = (v = "") => {
  const raw = (v || "").toUpperCase().replace(/\./g, "").replace(/[^0-9Kk-]/g, "");
  if (!raw) return "";
  if (/-/.test(raw)) return raw.replace(/k$/, "K");
  if (raw.length >= 2) return `${raw.slice(0, -1)}-${raw.slice(-1)}`.replace(/k$/, "K");
  return raw;
};

const iconForFile = (name = "", type = "") => {
  const e = ext(name);
  if (type.startsWith("image/")) return <ImageIcon className="w-4 h-4" />;
  if (e === "pdf" || type === "application/pdf") return <FileText className="w-4 h-4" />;
  if (["xls", "xlsx", "csv"].includes(e)) return <FileSpreadsheet className="w-4 h-4" />;
  if (["zip", "rar", "7z"].includes(e) || type.includes("zip"))
    return <FileArchive className="w-4 h-4" />;
  return <FileIcon className="w-4 h-4" />;
};

/* ---------- UI primitives ---------- */
const Field = ({ label, required, hint, error, children }) => (
  <label className="block">
    <div className="flex items-center justify-between">
      <span className="block text-[13px] font-semibold text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {error ? (
        <span className="text-[11px] font-medium text-rose-600">{error}</span>
      ) : hint ? (
        <span className="text-[11px] text-slate-500">{hint}</span>
      ) : null}
    </div>
    <div className="mt-1.5">{children}</div>
  </label>
);

const baseInput =
  "w-full h-11 rounded-xl border bg-white px-3 outline-none transition focus:ring-2 disabled:opacity-70";
const withIconWrap = "relative";
const leftIconCls =
  "pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400";
const inputWithIcon = "pl-10";

const Input = ({ icon, error, className = "", ...props }) => (
  <div className={withIconWrap}>
    {icon && <span className={leftIconCls}>{icon}</span>}
    <input
      {...props}
      className={`${baseInput} ${icon ? inputWithIcon : ""} ${
        error ? "border-rose-300 focus:ring-rose-300" : "border-slate-300 focus:ring-[#0488AF]"
      } ${className}`}
    />
  </div>
);

const Select = ({ icon, error, className = "", children, ...props }) => (
  <div className="relative">
    {icon && <span className={leftIconCls}>{icon}</span>}
    <select
      {...props}
      className={`${baseInput} appearance-none ${icon ? inputWithIcon : ""} ${
        error ? "border-rose-300 focus:ring-rose-300" : "border-slate-300 focus:ring-[#0488AF]"
      } ${className}`}
    >
      {children}
    </select>
    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
  </div>
);

const TextArea = ({ icon, error, className = "", rows = 6, ...props }) => (
  <div className={withIconWrap}>
    {icon && <span className={`${leftIconCls} items-start pt-2`}>{icon}</span>}
    <textarea
      rows={rows}
      {...props}
      className={`w-full rounded-xl border bg-white px-3 py-2 outline-none transition ${
        icon ? "pl-10" : ""
      } ${error ? "border-rose-300 focus:ring-rose-300" : "border-slate-300 focus:ring-[#0488AF]"} ${className}`}
    />
  </div>
);

/* Encabezado con “banda” */
const SectionBanner = ({ n, title, subtitle, icon }) => (
  <div className="relative -mx-2 sm:-mx-3 mb-4">
    <div
      className="absolute inset-x-0 -inset-y-2 rounded-2xl
        bg-gradient-to-r from-cyan-100/70 via-sky-100/45 to-transparent
        ring-1 ring-cyan-200/60 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]
        backdrop-blur-[1px]"
    />
    <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-cyan-200/40 blur-2xl" />
    <div className="absolute -bottom-8 left-24 h-24 w-24 rounded-full bg-indigo-200/40 blur-2xl" />
    <div className="relative px-2 sm:px-3 py-3">
      <div className="flex items-start gap-3">
        <span
          className="inline-flex h-7 shrink-0 items-center gap-2 rounded-full
              bg-white/70 border border-cyan-300/60 text-cyan-700 px-2.5 text-[12px] font-semibold"
        >
          {icon || <Sparkles className="h-3.5 w-3.5" />} <span className="tabular-nums">#{n}</span>
        </span>
        <div>
          <div className="text-[15px] font-bold text-slate-800 leading-tight">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-500">{subtitle}</div>}
        </div>
      </div>
    </div>
  </div>
);

const DropInput = ({ label, hint, accept, multiple, onFiles }) => {
  const [hover, setHover] = useState(false);
  return (
    <div
      className={`rounded-xl border-2 border-dashed p-4 transition ${
        hover ? "border-[#0488AF] bg-cyan-50/40" : "border-slate-300 bg-gradient-to-br from-white to-slate-50"
      }`}
      onDragOver={(e) => {
        e.preventDefault();
        setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault();
        setHover(false);
        onFiles?.(Array.from(e.dataTransfer.files || []));
      }}
    >
      <div className="flex items-center gap-3 text-slate-700">
        <UploadCloud className="opacity-80" />
        <div className="flex-1">
          <div className="font-semibold">{label}</div>
          {hint && <div className="text-xs text-slate-500">{hint}</div>}
        </div>
        <label className="relative inline-flex items-center justify-center h-10 px-3 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 cursor-pointer">
          <FileUp size={18} className="mr-2" />
          Seleccionar
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            className="absolute inset-0 opacity-0 cursor-pointer"
            onChange={(e) => onFiles?.(Array.from(e.target.files || []))}
          />
        </label>
      </div>
    </div>
  );
};

const ImageThumb = ({ file, className = "h-10 w-10 rounded-lg object-cover" }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  if (!file) return null;
  if (!file.type.startsWith("image/")) {
    return (
      <div className="h-10 w-10 grid place-items-center rounded-lg bg-slate-100 text-slate-500">
        <ImageIcon className="w-5 h-5" />
      </div>
    );
  }
  return <img src={src} alt={file.name} className={className} />;
};

export default function MembershipApply() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [lock, setLock] = useState(false);    // para deshabilitar botones
  const submittingRef = useRef(false);        // bloqueo inmediato anti-doble submit

  // ---- Form state ----
  const [empresa, setEmpresa] = useState({
    razon_social: "",
    nombre_comercial: "",
    rut: "",
    tamano: "",
    direccion_matriz: "",
    region: "",
    provincia: "",
    comuna: "",
    pagina_web: "",
    numero_trabajadores: "",
    industria: "",
    periodicidad_pago: "",
    motivo_ingreso: "",
    aporte_region: "",
  });

  const [titular, setTitular] = useState({ nombre: "", correo: "", movil: "", cargo: "" });
  const [contacto, setContacto] = useState({ nombre: "", correo: "", movil: "", cargo: "" });

  // NUEVO: contacto de cobranza
  const [cobranza, setCobranza] = useState({
    nombre: "",
    correo: "",
    telefono: "",
    cargo: "",
    area: "",
  });

  // ===== Región / Provincia / Comuna (listas en cascada) =====
  const regiones = regionsData?.Regions ?? [];
  const [regionIdx, setRegionIdx] = useState(-1);
  const [provIdx, setProvIdx] = useState(-1);
  const provincias = regionIdx >= 0 ? regiones[regionIdx]?.provinces ?? [] : [];
  const comunas = provIdx >= 0 ? provincias[provIdx]?.districts ?? [] : [];

  // Archivos
  const [carpetaTributaria, setCarpetaTributaria] = useState([]);
  const [logoAlta, setLogoAlta] = useState([]);
  const [cartaRec, setCartaRec] = useState(null); // PDF obligatorio

  // Ruta al modelo de carta (archivo ubicado en /public)
  const letterTemplateHref = encodeURI(
    "/Carta tipo recomendación -Postulación ingreso a CIDERE.docx"
  );

  const steps = useMemo(
    () => [
      { n: 1, t: "Postula", d: "Ten a mano tu carpeta tributaria y carta de recomendación firmada por uno de nuestros socios actuales." },
      { n: 2, t: "Carta", d: "Completa el formulario con la información básica de tu organización." },
      { n: 3, t: "Revisión", d: "Una vez recepcionada tu información es revisada por el área de vinculación empresarial para luego ser presentada en la próxima sesión de directorio." },
      { n: 4, t: "Resolución", d: "En sesión de directorio se aprueban los nuevos ingresos. La decisión es comunicada por el área de vinculación empresarial vía correo electrónico." },
    ],
    []
  );

  /* ---------- Validaciones ---------- */
  const errors = {
    razon_social: submitted && !clean(empresa.razon_social) ? "Obligatorio" : "",
    rut: submitted && !clean(empresa.rut) ? "Obligatorio" : "",
  };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const titularCorreoErr =
    submitted && clean(titular.correo) && !emailRegex.test(titular.correo)
      ? "Email inválido"
      : "";
  const contactoCorreoErr =
    submitted && clean(contacto.correo) && !emailRegex.test(contacto.correo)
      ? "Email inválido"
      : "";
  const cobranzaErrors = {
    nombre: submitted && !clean(cobranza.nombre) ? "Obligatorio" : "",
    correo:
      submitted && (!clean(cobranza.correo)
        ? "Obligatorio"
        : !emailRegex.test(cobranza.correo)
        ? "Email inválido"
        : ""),
    telefono: submitted && !clean(cobranza.telefono) ? "Obligatorio" : "",
    cargo: submitted && !clean(cobranza.cargo) ? "Obligatorio" : "",
    area: submitted && !clean(cobranza.area) ? "Obligatorio" : "",
  };
  const regionErr = submitted && !clean(empresa.region) ? "Obligatorio" : "";
  const provinciaErr = submitted && !clean(empresa.provincia) ? "Obligatorio" : "";
  const comunaErr = submitted && !clean(empresa.comuna) ? "Obligatorio" : "";

  const formId = "applyForm"; // para requestSubmit() desde el sticky CTA

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || lock || busy) return; // doble protección
    setSubmitted(true);

    // validaciones mínimas
    if (errors.razon_social || errors.rut || titularCorreoErr || contactoCorreoErr) {
      Swal.fire("Revisa el formulario", "Hay campos obligatorios o con formato inválido.", "warning");
      return;
    }
    if (Object.values(cobranzaErrors).some(Boolean) || regionErr || provinciaErr || comunaErr) {
      Swal.fire(
        "Revisa los datos",
        "Completa Región, Provincia y Comuna y corrige los campos marcados.",
        "warning"
      );
      return;
    }

    // Validaciones de archivos
    if (!cartaRec) {
      Swal.fire("Falta la carta", "Debes adjuntar la carta de recomendación en PDF.", "warning");
      return;
    }
    if (!isPdf(cartaRec)) {
      Swal.fire("Formato inválido", "La carta debe ser un archivo .pdf.", "warning");
      return;
    }
    if (carpetaTributaria.some((f) => !isPdf(f))) {
      Swal.fire("Formato inválido", "La carpeta tributaria debe contener solo archivos PDF.", "warning");
      return;
    }
    if (logoAlta[0] && !isJpgPng(logoAlta[0])) {
      Swal.fire("Formato inválido", "El logo debe ser JPG o PNG.", "warning");
      return;
    }

    // Bloqueo inmediato antes del Swal
    submittingRef.current = true;
    setLock(true);

    const confirm = await Swal.fire({
      title: "¿Enviar postulación?",
      text: "Confirmas que tus datos y documentos son correctos.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, enviar",
      cancelButtonText: "Revisar",
      confirmButtonColor: "#0488AF",
      cancelButtonColor: "#C42251",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) {
      setLock(false);
      submittingRef.current = false;
      return;
    }

    try {
      setBusy(true);

      // Normalizamos RUT al enviar
      const empresaSafe = {
        ...empresa,
        rut: normalizeRut(empresa.rut),
      };

      const payload = {
        timestamp: new Date().toISOString(),
        empresa: empresaSafe,
        titular,
        contacto,
        cobranza,
        adjuntos: { carpeta_tributaria: [], logo_alta: [] },
      };

      const form = new FormData();
      form.append("payload", JSON.stringify(payload));
      carpetaTributaria.forEach((f) => form.append("carpetaTributaria[]", f));
      if (logoAlta[0]) form.append("logoAlta", logoAlta[0]);
      form.append("cartaRecomendacion", cartaRec);

      const baseEnv = import.meta.env.VITE_API_URL || "";
      const base = baseEnv.replace(/\/+$/, "");
      const idemKey = `${normalizeRut(empresa.rut)}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

      const resp = await fetch(`${base}/membership-applications`, {
        method: "POST",
        body: form,
        headers: { "X-Idempotency-Key": idemKey },
      });

      if (!resp.ok) {
        let msg = "Error al enviar postulación";
        try {
          msg = (await resp.json())?.message || msg;
        } catch {}
        throw new Error(msg);
      }

      await Swal.fire(
        "¡Enviado!",
        "Tu postulación fue recibida. Estado inicial: Pendiente.",
        "success"
      );
      // Redirigir al login después de cerrar el modal
      setTimeout(() => {
        window.location.href = "/";
      }, 500);
    } catch (err) {
      Swal.fire("Error", err.message || "No se pudo enviar la postulación.", "error");
    } finally {
      setBusy(false);
      setLock(false);
      submittingRef.current = false;
    }
  };

  const removeCarpetaItem = (idx) =>
    setCarpetaTributaria((prev) => prev.filter((_, i) => i !== idx));
  const clearCarpeta = () => setCarpetaTributaria([]);
  const clearLogo = () => setLogoAlta([]);

  return (
    <div
      className="min-h-screen flex flex-col justify-between font-sans bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: "url('/Gráficas web RDS-02.jpg')" }}
    >
      <div className="absolute inset-0 bg-slate-900/40" />

      <main className="relative flex-1 w-full mx-auto max-w-7xl px-4 md:px-6 lg:px-10 py-6 md:py-10">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center text-white/90 hover:text-white"
        >
          <ArrowLeft className="mr-2" /> Volver
        </button>

        {/* GRID INVERTIDO: IZQ panel “Únete…”, DER formulario */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
          {/* IZQUIERDA: Panel lateral (sticky en desktop) */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <Appear from="translate-y-4" delay={30}>
              <div
                className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-white/70"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/85 via-sky-800/75 to-blue-900/85" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.08),transparent_60%)]" />
                <img
                  src="/RDS - LOGO VERTICAL-06.png"
                  alt="RDS"
                  className="absolute top-4 right-4 h-8 opacity-80"
                />

                <section className="relative px-6 sm:px-7 md:px-8 py-7 sm:py-8 text-white">
                  <h2 className="mt-3 text-2xl md:text-3xl font-bold leading-tight text-center">
                    Únete a la Red de Socios
                  </h2>


                  <div className="mt-7">
                    <div className="text-[11px] tracking-wider text-white/70 font-semibold mb-2.5">
                      CÓMO FUNCIONA
                    </div>
                    <div className="flex flex-col gap-3">
                      {steps.map((s) => (
                        <div
                          key={s.n}
                          className="flex flex-col rounded-xl border border-white/20 bg-white/5 p-3"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <span className="inline-flex w-6 h-6 items-center justify-center rounded-full bg-white/20 text-white text-xs font-bold">
                              {s.n}
                            </span>
                            <span className="font-semibold">{s.t}</span>
                          </div>
                          <p className="text-sm text-white/85">{s.d}</p>
                        </div>
                      ))}
                    </div>

                    {/* Botón blanco para descargar el formato de carta */}
                    <div className="mt-4 text-center">
                      <a
                        href={letterTemplateHref}
                        download
                        className="inline-flex items-center gap-2 rounded-xl bg-white/95 text-slate-900 font-semibold px-4 h-10 border border-white/70 hover:bg-white transition"
                      >
                        <FileText className="h-4 w-4" />
                        Formato carta de recomendación
                      </a>
                    </div>
                  </div>
                </section>
              </div>
            </Appear>
          </div>

          {/* DERECHA: Panel formulario */}
          <Appear from="translate-y-4" delay={120}>
            <div className="bg-white/95 rounded-2xl shadow-xl p-6 sm:p-7">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <img src="/RDS - LOGO VERTICAL-01.png" alt="RDS" className="h-10" />
                  <h1 className="text-xl md:text-2xl font-bold text-[#0B3A66] text-center">
                    Postulación de membresía
                  </h1>
                </div>
              </div>

              {/* id para requestSubmit() */}
              <form id="applyForm" onSubmit={onSubmit} className="space-y-7">
                {/* Empresa */}
                <div className="flex items-start gap-3 mb-3">
                  <span className="inline-flex h-7 shrink-0 items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 px-2.5 text-[12px] font-semibold text-cyan-700">
                    <Building2 className="h-3.5 w-3.5" /> <span className="tabular-nums">#1</span>
                  </span>
                  <div>
                    <div className="text-[15px] font-bold text-slate-800 leading-tight">
                      Datos de la empresa
                    </div>
                    <div className="text-[12px] text-slate-500">
                      Cuéntanos lo básico para comenzar la evaluación.
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Razón social" required error={errors.razon_social}>
                    <Input
                      required
                      icon={<Landmark className="h-4 w-4" />}
                      value={empresa.razon_social}
                      onChange={(e) => setEmpresa((s) => ({ ...s, razon_social: e.target.value }))}
                      placeholder="Ej: ACME SpA"
                      error={!!errors.razon_social}
                    />
                  </Field>

                  <Field label="Nombre comercial" required>
                    <Input
                      required
                      icon={<Briefcase className="h-4 w-4" />}
                      value={empresa.nombre_comercial}
                      onChange={(e) =>
                        setEmpresa((s) => ({ ...s, nombre_comercial: e.target.value }))
                      }
                      placeholder="Ej: ACME"
                    />
                  </Field>

                  <Field label="RUT" required error={errors.rut} hint="Sin puntos y con guion">
                    <Input
                      required
                      icon={<IdCard className="h-4 w-4" />}
                      value={empresa.rut}
                      onChange={(e) => setEmpresa((s) => ({ ...s, rut: formatRUT(e.target.value) }))}
                      placeholder="12345678-9"
                      error={!!errors.rut}
                    />
                  </Field>

                  <Field label="Tamaño de empresa" required hint="Según ventas anuales">
                    <Select
                      required
                      icon={<Users2 className="h-4 w-4" />}
                      value={empresa.tamano}
                      onChange={(e) => setEmpresa((s) => ({ ...s, tamano: e.target.value }))}
                    >
                      <option value="">Selecciona…</option>
                      {companySizes.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Dirección (casa matriz)" required>
                    <Input
                      required
                      icon={<MapPin className="h-4 w-4" />}
                      value={empresa.direccion_matriz}
                      onChange={(e) =>
                        setEmpresa((s) => ({ ...s, direccion_matriz: e.target.value }))
                      }
                      placeholder="Calle, número, ciudad"
                    />
                  </Field>

                  {/* Región */}
                  <Field label="Región" required error={regionErr}>
                    <Select
                      required
                      icon={<MapPin className="h-4 w-4" />}
                      value={regionIdx >= 0 ? regiones[regionIdx].name : ""}
                      onChange={(e) => {
                        const idx = regiones.findIndex((r) => r.name === e.target.value);
                        setRegionIdx(idx);
                        setProvIdx(-1);
                        setEmpresa((s) => ({
                          ...s,
                          region: e.target.value,
                          provincia: "",
                          comuna: "",
                        }));
                      }}
                    >
                      <option value="">Selecciona…</option>
                      {regiones.map((r) => (
                        <option key={r.name} value={r.name}>
                          {r.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  {/* Provincia */}
                  <Field label="Provincia" required error={provinciaErr}>
                    <Select
                      required
                      icon={<MapPin className="h-4 w-4" />}
                      value={provIdx >= 0 ? provincias[provIdx].name : ""}
                      onChange={(e) => {
                        const idx = provincias.findIndex((p) => p.name === e.target.value);
                        setProvIdx(idx);
                        setEmpresa((s) => ({ ...s, provincia: e.target.value, comuna: "" }));
                      }}
                      disabled={regionIdx < 0}
                    >
                      <option value="">
                        {regionIdx < 0 ? "Selecciona región primero" : "Selecciona…"}
                      </option>
                      {provincias.map((p) => (
                        <option key={p.name} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  {/* Comuna */}
                  <Field label="Comuna" required error={comunaErr}>
                    <Select
                      required
                      icon={<MapPin className="h-4 w-4" />}
                      value={empresa.comuna}
                      onChange={(e) => setEmpresa((s) => ({ ...s, comuna: e.target.value }))}
                      disabled={provIdx < 0}
                    >
                      <option value="">
                        {provIdx < 0 ? "Selecciona provincia primero" : "Selecciona…"}
                      </option>
                      {comunas.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Página web" required hint="Incluye https://">
                    <Input
                      required
                      type="url"
                      icon={<Globe className="h-4 w-4" />}
                      value={empresa.pagina_web}
                      onChange={(e) => setEmpresa((s) => ({ ...s, pagina_web: e.target.value }))}
                      placeholder="https://…"
                    />
                  </Field>

                  <Field label="N° trabajadores" required>
                    <Select
                      required
                      icon={<Users2 className="h-4 w-4" />}
                      value={empresa.numero_trabajadores}
                      onChange={(e) =>
                        setEmpresa((s) => ({ ...s, numero_trabajadores: e.target.value }))
                      }
                    >
                      <option value="">Selecciona…</option>
                      {workersRanges.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Industria" required>
                    <Select
                      required
                      icon={<Briefcase className="h-4 w-4" />}
                      value={empresa.industria}
                      onChange={(e) => setEmpresa((s) => ({ ...s, industria: e.target.value }))}
                    >
                      <option value="">Selecciona…</option>
                      {industries.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Field>

                  <Field label="Periodicidad de pago" required>
                    <Select
                      required
                      icon={<CalendarClock className="h-4 w-4" />}
                      value={empresa.periodicidad_pago}
                      onChange={(e) =>
                        setEmpresa((s) => ({ ...s, periodicidad_pago: e.target.value }))
                      }
                    >
                      <option value="">Selecciona…</option>
                      {periodicities.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </div>

                {/* Textareas */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Motivo para ingresar">
                    <TextArea
                      required
                      className="min-h-[160px]"
                      value={empresa.motivo_ingreso}
                      onChange={(e) => setEmpresa((s) => ({ ...s, motivo_ingreso: e.target.value }))}
                    />
                  </Field>
                  <Field label="Aporte a la región">
                    <TextArea
                      required
                      className="min-h-[160px]"
                      value={empresa.aporte_region}
                      onChange={(e) => setEmpresa((s) => ({ ...s, aporte_region: e.target.value }))}
                    />
                  </Field>
                </div>

                {/* Contactos */}
                <SectionBanner
                  n={2}
                  title="Contactos"
                  subtitle="Titular y contraparte operativa de la empresa."
                  icon={<UserIcon className="h-3.5 w-3.5" />}
                />
                <div className="relative rounded-2xl border border-cyan-200/60 bg-gradient-to-br from-cyan-50 via-sky-50 to-indigo-50 p-4 sm:p-6 shadow-sm">
                  <div className="pointer-events-none absolute -top-12 -left-10 h-40 w-40 rounded-full bg-cyan-200/40 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-14 -right-10 h-48 w-48 rounded-full bg-indigo-200/40 blur-3xl" />

                  <div className="relative grid sm:grid-cols-2 gap-6">
                    {/* Titular */}
                    <div className="rounded-xl border border-white/60 bg-white/70 backdrop-blur-sm p-4 sm:p-5 shadow-sm">
                      <div className="text-[13px] font-semibold text-slate-700 flex items-center gap-2 mb-3">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-cyan-100 text-cyan-700">
                          <UserIcon className="h-4 w-4" />
                        </span>
                        Titular / Representante
                      </div>
                      <div className="space-y-3.5">
                        <Field label="Nombre" required>
                          <Input
                            required
                            icon={<UserIcon className="h-4 w-4" />}
                            value={titular.nombre}
                            onChange={(e) => setTitular((s) => ({ ...s, nombre: e.target.value }))}
                          />
                        </Field>
                        <Field label="Correo" required error={titularCorreoErr}>
                          <Input
                            required
                            type="email"
                            icon={<Mail className="h-4 w-4" />}
                            value={titular.correo}
                            onChange={(e) => setTitular((s) => ({ ...s, correo: e.target.value }))}
                            error={!!titularCorreoErr}
                          />
                        </Field>
                        <Field label="Móvil" required hint="+56 9 …">
                          <Input
                            required
                            icon={<Phone className="h-4 w-4" />}
                            value={titular.movil}
                            onChange={(e) => setTitular((s) => ({ ...s, movil: e.target.value }))}
                          />
                        </Field>
                        <Field label="Cargo" required>
                          <Input
                            required
                            icon={<Briefcase className="h-4 w-4" />}
                            value={titular.cargo}
                            onChange={(e) => setTitular((s) => ({ ...s, cargo: e.target.value }))}
                          />
                        </Field>
                      </div>
                    </div>

                    {/* Contacto empresa */}
                    <div className="rounded-xl border border-white/60 bg-white/70 backdrop-blur-sm p-4 sm:p-5 shadow-sm">
                      <div className="text-[13px] font-semibold text-slate-700 flex items-center gap-2 mb-3">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                          <UserIcon className="h-4 w-4" />
                        </span>
                        Contacto empresa
                      </div>
                      <div className="space-y-3.5">
                        <Field label="Nombre" required>
                          <Input
                            required
                            icon={<UserIcon className="h-4 w-4" />}
                            value={contacto.nombre}
                            onChange={(e) => setContacto((s) => ({ ...s, nombre: e.target.value }))}
                          />
                        </Field>
                        <Field label="Correo" required error={contactoCorreoErr}>
                          <Input
                            required
                            type="email"
                            icon={<Mail className="h-4 w-4" />}
                            value={contacto.correo}
                            onChange={(e) => setContacto((s) => ({ ...s, correo: e.target.value }))}
                            error={!!contactoCorreoErr}
                          />
                        </Field>
                        <Field label="Móvil" required hint="+56 9 …">
                          <Input
                            required
                            icon={<Phone className="h-4 w-4" />}
                            value={contacto.movil}
                            onChange={(e) => setContacto((s) => ({ ...s, movil: e.target.value }))}
                          />
                        </Field>
                        <Field label="Cargo" required>
                          <Input
                            required
                            icon={<Briefcase className="h-4 w-4" />}
                            value={contacto.cargo}
                            onChange={(e) => setContacto((s) => ({ ...s, cargo: e.target.value }))}
                          />
                        </Field>
                      </div>
                    </div>
                  </div>
                </div>

                {/* NUEVO: Contacto de cobranza */}
                <SectionBanner
                  n={3}
                  title="Contacto de cobranza"
                  subtitle="Datos para facturación y pagos."
                  icon={<HandCoins className="h-3.5 w-3.5" />}
                />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Nombre completo" required error={cobranzaErrors.nombre}>
                    <Input
                      required
                      value={cobranza.nombre}
                      onChange={(e) => setCobranza((s) => ({ ...s, nombre: e.target.value }))}
                      error={!!cobranzaErrors.nombre}
                    />
                  </Field>
                  <Field label="Correo electrónico" required error={cobranzaErrors.correo}>
                    <Input
                      required
                      type="email"
                      value={cobranza.correo}
                      onChange={(e) => setCobranza((s) => ({ ...s, correo: e.target.value }))}
                      error={!!cobranzaErrors.correo}
                    />
                  </Field>
                  <Field label="Teléfono" required error={cobranzaErrors.telefono}>
                    <Input
                      required
                      value={cobranza.telefono}
                      onChange={(e) => setCobranza((s) => ({ ...s, telefono: e.target.value }))}
                      error={!!cobranzaErrors.telefono}
                    />
                  </Field>
                  <Field label="Cargo" required error={cobranzaErrors.cargo}>
                    <Input
                      required
                      value={cobranza.cargo}
                      onChange={(e) => setCobranza((s) => ({ ...s, cargo: e.target.value }))}
                      error={!!cobranzaErrors.cargo}
                    />
                  </Field>
                  <Field label="Área" required error={cobranzaErrors.area}>
                    <Input
                      required
                      value={cobranza.area}
                      onChange={(e) => setCobranza((s) => ({ ...s, area: e.target.value }))}
                      error={!!cobranzaErrors.area}
                    />
                  </Field>
                </div>

                {/* Adjuntos */}
                <div className="flex items-start gap-3 mb-3 mt-7">
                  <span className="inline-flex h-7 shrink-0 items-center gap-2 rounded-full bg-cyan-50 border border-cyan-200 px-2.5 text-[12px] font-semibold text-cyan-700">
                    <FileIcon className="h-3.5 w-3.5" /> <span className="tabular-nums">#4</span>
                  </span>
                  <div>
                    <div className="text-[15px] font-bold text-slate-800 leading-tight">Adjuntos</div>
                    <div className="text-[12px] text-slate-500">
                      Comparte tu carpeta tributaria (<b>PDF</b>), la carta de recomendación (<b>PDF</b>) y el logo en alta (<b>JPG/PNG</b>).
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* Carpeta tributaria: SOLO PDF */}
                  <DropInput
                    label="Carpeta tributaria (PDF obligatorio)"
                    hint="Puedes arrastrar varios archivos .pdf"
                    accept="application/pdf,.pdf"
                    multiple
                    onFiles={(files) => setCarpetaTributaria((files || []).filter(isPdf))}
                  />

                  {carpetaTributaria.length > 0 && (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-slate-600">
                          {carpetaTributaria.length} archivo(s) seleccionado(s)
                        </div>
                        <button
                          type="button"
                          onClick={clearCarpeta}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800"
                          title="Quitar todos"
                        >
                          <Trash2 className="w-4 h-4" /> Limpiar
                        </button>
                      </div>
                      <ul className="mt-2 space-y-2 max-h-48 overflow-auto pr-1">
                        {carpetaTributaria.map((f, i) => (
                          <li
                            key={`${f.name}-${i}`}
                            className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-200 px-3 py-2"
                          >
                            <div className="min-w-0 flex items-center gap-2">
                              <div className="h-8 w-8 grid place-items-center rounded-md bg-slate-100 text-slate-600">
                                {iconForFile(f.name, f.type)}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-800 truncate">
                                  {f.name}
                                </div>
                                <div className="text-xs text-slate-500">{formatBytes(f.size)}</div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeCarpetaItem(i)}
                              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-600"
                              title="Quitar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Carta de recomendación: OBLIGATORIA, SOLO PDF */}
                  <DropInput
                    label="Carta de recomendación (PDF obligatorio)"
                    hint="Debe ser un único archivo .pdf"
                    accept="application/pdf,.pdf"
                    multiple={false}
                    onFiles={(files) => setCartaRec((files || []).filter(isPdf)[0] || null)}
                  />

                  {cartaRec && (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-slate-600">Carta seleccionada</div>
                        <button
                          type="button"
                          onClick={() => setCartaRec(null)}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800"
                          title="Quitar carta"
                        >
                          <Trash2 className="w-4 h-4" /> Quitar
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="h-8 w-8 grid place-items-center rounded-md bg-slate-100 text-slate-600">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">
                            {cartaRec.name}
                          </div>
                          <div className="text-xs text-slate-500">{formatBytes(cartaRec.size)}</div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Logo: SOLO JPG/PNG */}
                  <DropInput
                    label="Logo en alta resolución (PNG o JPG)"
                    hint="Máximo un archivo."
                    accept="image/png,image/jpeg"
                    multiple={false}
                    onFiles={(files) => {
                      const one = (files || [])[0];
                      setLogoAlta(one && isJpgPng(one) ? [one] : []);
                    }}
                  />

                  {logoAlta[0] && (
                    <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold text-slate-600">Logo seleccionado</div>
                        <button
                          type="button"
                          onClick={clearLogo}
                          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-800"
                          title="Quitar logo"
                        >
                          <Trash2 className="w-4 h-4" /> Quitar
                        </button>
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <ImageThumb file={logoAlta[0]} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">
                            {logoAlta[0].name}
                          </div>
                          <div className="text-xs text-slate-500">{formatBytes(logoAlta[0].size)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* CTA principal dentro del form */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={busy || lock}
                    className="inline-flex items-center justify-center h-11 px-5 rounded-xl bg-[#0488AF] text-white font-semibold hover:bg[#037a98] disabled:opacity-70 shadow-sm"
                  >
                    {busy || lock ? <Loader2 className="animate-spin mr-2" /> : null}
                    Enviar postulación
                  </button>
                </div>
              </form>

              {/* Sticky CTA en mobile — fuera del form, usa requestSubmit() */}
              <div className="lg:hidden sticky bottom-4">
                <div className="mt-2 rounded-xl bg-white shadow-lg border border-slate-200 p-3">
                  <button
                    type="button"
                    disabled={busy || lock}
                    onClick={() => document.getElementById("applyForm")?.requestSubmit()}
                    className="w-full inline-flex items-center justify-center h-11 px-5 rounded-xl bg-[#0488AF] text-white font-semibold hover:bg-[#037a98] disabled:opacity-70"
                  >
                    {busy || lock ? <Loader2 className="animate-spin mr-2" /> : null}
                    Enviar postulación
                  </button>
                </div>
              </div>
            </div>
          </Appear>
        </div>
      </main>

      <Footer />
    </div>
  );
}
